import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Copy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteTemplatesContent() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "General",
    default_client_type: "Business",
    garments: [],
    artwork_groups: [],
  });

  const getToken = () => localStorage.getItem("auth_token");

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/templates`, { headers });
      setTemplates(res.data.templates || []);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error("Failed to load templates", error);
      toast.error("Failed to load quote templates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error("Template name is required");
      return;
    }

    setIsSaving(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, formData, { headers });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/templates`, formData, { headers });
        toast.success("Template created");
      }

      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ name: "", description: "", category: "General", default_client_type: "Business", garments: [], artwork_groups: [] });
      loadTemplates();
    } catch (error) {
      console.error("Failed to save template", error);
      toast.error("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/templates/${id}`, { headers });
      toast.success("Template deleted");
      setDeleteId(null);
      loadTemplates();
    } catch (error) {
      console.error("Failed to delete template", error);
      toast.error("Failed to delete template");
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/templates`, {
        ...template,
        id: undefined,
        name: `${template.name} (Copy)`,
      }, { headers });
      toast.success("Template duplicated");
      loadTemplates();
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            Quote Templates
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create reusable templates for common quote configurations
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditingTemplate(null);
            setFormData({ name: "", description: "", category: "General", default_client_type: "Business", garments: [], artwork_groups: [] });
            setShowModal(true);
          }}
          className="h-9 text-xs bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="w-3 h-3 mr-1.5" />
          New Template
        </Button>
      </div>

      {/* Templates List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No quote templates yet</p>
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowModal(true)}
            className="mt-2 text-amber-600"
          >
            Create your first template
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((template) => (
            <div
              key={template.id}
              className="rounded-lg border border-gray-100 bg-gray-50/50 overflow-hidden"
            >
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100/50"
                onClick={() => setExpandedId(expandedId === template.id ? null : template.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedId === template.id ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                  <div>
                    <span className="font-medium text-gray-900">{template.name}</span>
                    {template.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {template.category && (
                    <Badge variant="outline" className="text-[10px]">
                      {template.category}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-[10px]">
                    {template.garments?.length || 0} garments
                  </Badge>
                  <Badge variant="secondary" className="text-[10px]">
                    {template.artwork_groups?.length || 0} prints
                  </Badge>
                  <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => duplicateTemplate(template)}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingTemplate(template);
                        setFormData(template);
                        setShowModal(true);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(template.id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {expandedId === template.id && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-white">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Garments</h4>
                      {template.garments?.length > 0 ? (
                        <ul className="space-y-1 text-xs text-gray-600">
                          {template.garments.map((g, idx) => (
                            <li key={idx}>• {g.name} (×{g.quantity})</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-400">No garments</p>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Print Groups</h4>
                      {template.artwork_groups?.length > 0 ? (
                        <ul className="space-y-1 text-xs text-gray-600">
                          {template.artwork_groups.map((ag, idx) => (
                            <li key={idx}>• {ag.name || `Print ${idx + 1}`} ({ag.size_tier || 'M'})</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-400">No print groups</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>
              Templates save garment and print configurations for quick reuse.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sports Club Package"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={2}
              />
            </div>
            <p className="text-xs text-gray-500">
              Tip: Create a new quote, configure it as needed, then save it as a template from the Quote Builder.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this quote template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
